import React, { useState } from 'react';
import FAQSection from '../components/utilities/FAQSection';

interface FormData {
  FormType: string;
  name: string;
  pronouns: string;
  email: string;
  phone: string;
  qualifications: string;
  specializations: string;
  preferredModality: string;
  experience: string;
  roleApplied: string;
  otherRole: string;
  preferredSupervisor: string;
  cv: string;
  presentation: string;
  acceptTerms: boolean;
}

const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwTjzZiXL1atnR-GC-KonwN-OntgzgqWwYrN9yE__ZRtKV-MvT0xDT1CwOO4WAnDdoE/exec';

const JoinUsPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const initialFormState = {
    FormType: 'Join Us',
    name: '',
    pronouns: '',
    email: '',
    phone: '',
    qualifications: '',
    specializations: '',
    preferredModality: '',
    experience: '',
    roleApplied: '',
    otherRole: '',
    preferredSupervisor: '',
    cv: '',
    presentation: '',
    acceptTerms: false
  };

  const [formData, setFormData] = useState<FormData>(initialFormState);

  const roles = [
    "Associate Therapist",
    "Senior-track Therapist",
    "Senior Therapist",
    "Other"
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'file') {
      const fileInput = e.target as HTMLInputElement;
      const file = fileInput.files?.[0] || null;
      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
    } else {
      const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
    }
  };

  //   const sendWhatsAppMessage = () => {
  //     const message = `new JOINING Request:
  // Name: ${formData.name}
  // Email: ${formData.email}
  // Phone: ${formData.phone}
  // Qualifications: ${formData.qualifications}
  // Specialization: ${formData.specializations}
  // Preferred Modality: ${formData.preferredModality}
  // Experience: ${formData.experience}
  // Role Applied: ${formData.roleApplied}
  // Other Role: ${formData.otherRole}
  // Preferred Supervisor: ${formData.preferredSupervisor}
  // CV: ${formData.cv}
  // Presentation: ${formData.presentation}`;

  //     const whatsappNumber = '918591049845';
  //     const encodedMessage = encodeURIComponent(message);
  //     const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
  //     window.open(whatsappUrl, '_blank');
  //   };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const submitToGoogleSheets = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      alert("Please accept the terms and conditions to continue!");
      return;
    }
    setIsSubmitting(true);

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          timeStamp: new Date().toISOString(),
          ...formData
        })
      });
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSubmitSuccess(true);
      //sendWhatsAppMessage();
      setFormData(initialFormState);
    } catch (error) {
      console.error('Error submitting form: ', error);
      alert('There was an error submitting the form. PLEASE TRY AGAIN');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.pronouns && formData.email && formData.phone;
      case 2:
        return formData.qualifications && formData.specializations &&
          formData.preferredModality && formData.experience &&
          formData.roleApplied &&
          (formData.roleApplied !== 'Other' || formData.otherRole);
      default:
        return true;
    }
  };

  // Form steps content
  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold mb-1 text-teal-900">Full Name <span className="text-rose-500">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="pronouns" className="block text-sm font-semibold mb-1 text-teal-900">Pronouns <span className="text-rose-500">*</span></label>
              <input
                type="text"
                id="pronouns"
                name="pronouns"
                required
                value={formData.pronouns}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="they/any"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold mb-1 text-teal-900">Email Address <span className="text-rose-500">*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold mb-1 text-teal-900">Phone Number <span className="text-rose-500">*</span></label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="Enter your phone number"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="qualifications" className="block text-sm font-semibold mb-1 text-teal-900">Qualifications <span className="text-rose-500">*</span></label>
              <textarea
                id="qualifications"
                name="qualifications"
                required
                value={formData.qualifications}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="List your degrees, certifications, and relevant qualifications"
              />
            </div>

            <div>
              <label htmlFor="specializations" className="block text-sm font-semibold mb-1 text-teal-900">Specializations <span className="text-rose-500">*</span></label>
              <textarea
                id="specializations"
                name="specializations"
                required
                value={formData.specializations}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="Your areas of specialty in therapy/counseling example - Trauma, Grief, Stress management, Relationship concerns, Gender and sexuality concerns ,Self-doubt issues ,"
              />
            </div>

            <div>
              <label htmlFor="preferredModality" className="block text-sm font-semibold mb-1 text-teal-900">
                Preferred Modality <span className="text-rose-500">*</span>
                <span className="font-normal text-gray-600 block mt-1">
                  (e.g., Psychodynamic, Humanistic, IFS, etc. - certification is optional)
                </span>
              </label>
              <textarea
                id="preferredModality"
                name="preferredModality"
                required
                value={formData.preferredModality}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                placeholder="Describe your preferred therapeutic modalities"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="experience" className="block text-sm font-semibold mb-1 text-teal-900">Years of Experience <span className="text-rose-500">*</span></label>
                <input
                  type="number"
                  id="experience"
                  name="experience"
                  required
                  min="0"
                  step="0.5"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                  placeholder="0.5"
                />
              </div>

              <div>
                <label htmlFor="roleApplied" className="block text-sm font-semibold mb-1 text-teal-900">Role Applied For <span className="text-rose-500">*</span></label>
                <select
                  id="roleApplied"
                  name="roleApplied"
                  required
                  value={formData.roleApplied}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500 appearance-none"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23115e59'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                    backgroundSize: "1.5em 1.5em"
                  }}
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData.roleApplied === 'Associate Therapist' && (
              <div>
                <label htmlFor="preferredSupervisor" className="block text-sm font-semibold mb-1 text-teal-900">
                  Preferred Supervisor
                  <span className="font-normal text-gray-600 block mt-1">
                    If applying for Associate Therapist role, do you have any preferences for your supervisor? Additionally, if you're applying for a Specific Supervisor, please add their name below.
                  </span>
                </label>
                <input
                  type="text"
                  id="preferredSupervisor"
                  name="preferredSupervisor"
                  value={formData.preferredSupervisor}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter preferred supervisor name (if any) or preferences"
                />
              </div>
            )}
            {formData.roleApplied === 'Other' && (
              <div>
                <label htmlFor="otherRole" className="block text-sm font-semibold mb-1 text-teal-900">Please specify the role <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  id="otherRole"
                  name="otherRole"
                  required
                  value={formData.otherRole}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                  placeholder="Specify the role you're applying for"
                />
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="cv" className="block text-sm font-semibold mb-1 text-teal-900">Latest CV (Link) <span className="text-rose-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <input
                  type="url"
                  id="cv"
                  name="cv"
                  required
                  value={formData.cv}
                  onChange={handleChange}
                  placeholder="https://drive.google.com/your-cv-file"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-600">Upload your CV to Google Drive, Dropbox, etc. and share the link</p>
            </div>

            {/* Replace just the presentation field section with this updated version */}
            <div>
              <label htmlFor="presentation" className="block text-sm font-semibold mb-1 text-teal-900 relative">
                Presentation/Video Link <span className="text-rose-500">*</span>
                <span className="font-normal text-gray-600 block mt-1">
                  Please provide a link to your video (up to 10 minutes) or presentation (up to 15 slides)
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="url"
                  id="presentation"
                  name="presentation"
                  required
                  value={formData.presentation}
                  onChange={handleChange}
                  placeholder="https://youtu.be/your-video-link"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border-0 shadow-md focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="mt-2 flex items-start p-3 bg-orange-50 rounded-lg border border-orange-100">
                <svg className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Not sure what to include? Your video/presentation should showcase your therapeutic approach and journey as an MHP.
                  </p>
                  <button
                    type="button"
                    className="mt-1 text-sm text-orange-600 hover:text-teal-800 font-medium inline-flex items-center"
                    onClick={() => {
                      // Find FAQ section
                      const faqSection = document.querySelector('.w-full.py-12');
                      if (faqSection) {
                        faqSection.scrollIntoView({ behavior: 'smooth' });

                        // Set search term to find the video/presentation FAQ
                        const searchInput = faqSection.querySelector('input[type="text"]');
                        if (searchInput) {
                          // Simply set the value
                          (searchInput as HTMLInputElement).value = "video or ppt";

                          // Create a new event instead of modifying the existing one
                          const customEvent = new Event('input', { bubbles: true });
                          searchInput.dispatchEvent(customEvent);

                          // If there's a React onChange handler, you might need to simulate it
                          // eslint-disable-next-line
                          const reactChangeEvent = { target: searchInput };
                          // Call the React change handler if you have access to it
                          // reactChangeHandler(reactChangeEvent);
                        }
                      }
                    }}
                  >
                    See detailed guidelines in FAQs
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-5 bg-teal-50 rounded-lg border border-teal-100 mt-6">
              <div className="flex items-start">
                <div className="flex items-center h-5 mt-1">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    name="acceptTerms"
                    required
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                </div>
                <label htmlFor="acceptTerms" className="ml-3 text-sm text-teal-900">
                  I confirm that I've read the roles, responsibilities, compensation, and fees involved with the respective role carefully before applying <span className="text-rose-500">*</span>
                </label>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-teal-700 text-white">
        <div className="absolute inset-0">
          <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 1463 360">
            <path className="text-teal-800 text-opacity-40" fill="currentColor" d="M0 0h1463v360H0z" />
            <path className="text-teal-600 text-opacity-40" fill="currentColor" d="M0 0c80.5 0 90.5 220 171 220s121-220 242-220 242 220 363 220 202-220 323-220 182 220 364 220V0z" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl font-fraunces-slab">Join Our Team</h1>
          <p className="mt-6 max-w-2xl text-xl text-teal-100">
            We're looking for passionate mental health professionals who are committed to making a positive impact in people's lives.
          </p>
        </div>
      </div>

      {/* Active Recruitment Banner - Add this right after the hero section
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-6 mx-auto max-w-7xl mb-10 mt-6 shadow-lg rounded-lg">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="flex-shrink-0 bg-white p-2 rounded-full mr-4">
              <svg className="h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-orange-600">We're Actively Onboarding!</h3>
              <p className="text-purple-100 text-lg">
                Immediate openings for Associate, Senior-track, and Senior Therapists
              </p>
            </div>
          </div>
          <a href="#application-form" className="whitespace-nowrap inline-flex items-center px-6 py-3 border-2 border-white rounded-md text-base font-medium text-purple-700 bg-white hover:bg-purple-50 transition-colors duration-200 shadow-md hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white">
            Apply Today
            <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>  */}

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">

          {/* Left Column - Value Proposition */}
          <div className="lg:col-span-5">
            <div className="sticky top-10">
              <h2 className="text-3xl font-bold text-teal-900 font-fraunces-slab">Why Join Our Practice?</h2>
              <p className="mt-4 text-lg text-gray-600">
                As a member of our therapeutic team, you'll get to work in a supportive environment that values growth, excellence, and compassionate care.
              </p>

              <div className="mt-10 space-y-6">
                {/* Benefit Items */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-teal-600 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-teal-900">Collaborative Team</h3>
                    <p className="mt-2 text-gray-600">
                      Work alongside experienced professionals in a supportive, growth-oriented environment.
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-teal-600 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-teal-900">Professional Development</h3>
                    <p className="mt-2 text-gray-600">
                      Access to supervision, continuous education, and opportunities to expand your expertise.
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-teal-600 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-teal-900">No Revenu Share Model</h3>
                    <p className="mt-2 text-gray-600">
                  You don't work for us but with us. You get to keep the entire counselling fee. 
                   </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-teal-600 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-teal-900">Flexible Work Options</h3>
                    <p className="mt-2 text-gray-600">
                      Options for both remote and in-person therapeutic services to suit your preferences.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-white shadow-md rounded-xl p-6 border border-teal-100">
                <h3 className="text-lg font-semibold text-teal-900">Have questions?</h3>
                <p className="mt-2 text-gray-600">
                  Contact our recruitment team directly at <a href="mailto:grehpraveshtherapy@gmail.com" className="text-teal-600 font-medium hover:text-teal-500">grehpraveshtherapy@gmail.com</a> or call us at <span className="text-teal-600 font-medium">8591049845</span>
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Application Form */}
          <div className="lg:col-span-7" id="application-form">
            {submitSuccess ? (
              <div className="bg-white rounded-2xl overflow-hidden shadow-xl py-12 px-8 text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-teal-100">
                  <svg className="h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="mt-5 text-2xl font-bold text-gray-900">Application Submitted Successfully!</h2>
                <p className="mt-4 text-lg text-gray-600">
                  Thank you for your interest in joining our team. We'll carefully review your application and get back to you as soon as possible.
                </p>
                <div className="mt-8">
                  <button
                    onClick={() => {
                      setSubmitSuccess(false);
                      setFormData(initialFormState);
                      setCurrentStep(1);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Submit Another Application
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
                {/* Form Header & Progress Bar */}
                <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-8 py-6 text-white">
                  <h2 className="text-2xl font-bold">Application Form</h2>
                  <p className="mt-1 text-teal-100">All fields marked with * are required</p>

                  {/* Progress Steps */}
                  <div className="mt-6 flex justify-between">
                    {[1, 2, 3].map((step) => (
                      <div key={step} className="flex flex-col items-center">
                        <div
                          className={`rounded-full h-10 w-10 flex items-center justify-center border-2 
                            ${currentStep >= step
                              ? 'bg-white border-white text-teal-600'
                              : 'border-teal-300 text-teal-300'}`}
                        >
                          {step}
                        </div>
                        <span
                          className={`mt-2 text-sm ${currentStep >= step
                            ? 'text-white'
                            : 'text-teal-300'}`}
                        >
                          {step === 1
                            ? 'Basic Info'
                            : step === 2
                              ? 'Experience'
                              : 'Documents'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Body */}
                <form onSubmit={submitToGoogleSheets} className="px-8 py-6">
                  {renderFormStep()}

                  {/* Form Navigation */}
                  <div className="mt-10 flex justify-between">
                    <button
                      type="button"
                      onClick={prevStep}
                      disabled={currentStep === 1}
                      className={`px-6 py-3 rounded-lg text-base font-medium transition-all
                        ${currentStep === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-teal-700 border border-teal-600 hover:bg-teal-50'}`}
                    >
                      Previous
                    </button>

                    {currentStep < totalSteps ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={!canProceedToNextStep()}
                        className={`px-6 py-3 rounded-lg text-base font-medium transition-all
                          ${!canProceedToNextStep()
                            ? 'bg-teal-300 text-white cursor-not-allowed'
                            : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'}`}
                      >
                        Continue
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting || !formData.cv || !formData.presentation || !formData.acceptTerms}
                        className={`px-6 py-3 rounded-lg text-base font-medium transition-all
                          ${isSubmitting || !formData.cv || !formData.presentation || !formData.acceptTerms
                            ? 'bg-teal-300 text-white cursor-not-allowed'
                            : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'}`}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </span>
                        ) : (
                          'Submit Application'
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        <FAQSection />
      </div>
    </div>
  )
}

export default JoinUsPage;